import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { tenantAPI, Tenant } from '@/services/api/tenants';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';

/**
 * Hook to fetch all tenants (for admins - bypasses RLS)
 * Use this in admin pages where you need to see all tenants
 * Only runs when session is established to ensure JWT is available for RPC calls
 *
 * Caching:
 *   - 5-minute stale window — tenants change rarely
 *   - placeholderData: keepPreviousData so navigation between admin pages
 *     and filter changes don't show a loading flash; old data stays
 *     painted while React Query refetches in the background
 *
 * Pass `{ enabled: false }` to defer the fetch (e.g. until a view needs it).
 */
export function useAllTenants({ enabled = true }: { enabled?: boolean } = {}) {
  const { session, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: ['tenants', 'all'],
    queryFn: () => tenantAPI.getAllTenants(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    placeholderData: keepPreviousData,
    // Only fetch when session is established (ensures JWT is available for RPC call)
    enabled: enabled && !!session && !authLoading
  });
}

/**
 * Hook to fetch tenants for the current user
 * Uses getMyTenants RPC which doesn't require super_admin role
 * Use this for regular users who should only see their assigned tenants
 */
export function useTenants() {
  const { user, session } = useAuth();

  return useQuery({
    queryKey: ['tenants', 'my', user?.id],
    queryFn: async () => {
      // Use getMyTenants - a non-admin RPC that returns only the current user's tenants
      // This is safe for all authenticated users (no super_admin required)
      return tenantAPI.getMyTenants();
    },
    // Only fetch when user is authenticated
    enabled: !!user?.id && !!session,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2
  });
}

/**
 * Hook to fetch a single tenant by ID
 */
export function useTenant(tenantId: string | null) {
  return useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => {
      if (!tenantId) throw new Error('Tenant ID is required');
      return tenantAPI.getTenantById(tenantId);
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2
  });
}

/**
 * Hook to fetch a tenant by slug
 */
export function useTenantBySlug(slug: string | null) {
  return useQuery({
    queryKey: ['tenant', 'slug', slug],
    queryFn: () => {
      if (!slug) throw new Error('Tenant slug is required');
      return tenantAPI.getTenantBySlug(slug);
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2
  });
}
